/**
 * AudioRecorder Component
 * 
 * Provides audio recording functionality with waveform visualization,
 * playback controls, and transcription integration.
 */

import React, { useState, useRef, useCallback, useEffect } from 'react';
import MicIcon from '@mui/icons-material/Mic';
import StopIcon from '@mui/icons-material/Stop';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import PauseIcon from '@mui/icons-material/Pause';
import DeleteIcon from '@mui/icons-material/Delete';
import SendIcon from '@mui/icons-material/Send';
import { useAuth } from '../../contexts/AuthContext';
import './AudioRecorder.css';

export interface AudioRecorderProps {
  onTranscription?: (text: string) => void;
  onRecordingComplete?: (audioBlob: Blob, audioUrl: string) => void;
  onError?: (error: string) => void;
  onDeductCredits?: (amount: number) => void; // Credit deduction callback
  disabled?: boolean;
  autoTranscribe?: boolean;
  language?: string;
  variant?: 'positive' | 'negative' | 'neutral' | 'purple' | 'warning';
  maxDurationSeconds?: number; // Maximum recording duration in seconds
}

interface RecordingState {
  isRecording: boolean;
  isPaused: boolean;
  isPlaying: boolean;
  isTranscribing: boolean;
  duration: number;
  audioBlob: Blob | null;
  audioUrl: string | null;
}

// Maximum recording duration: 5 minutes
const MAX_DURATION_SECONDS = 300;

export const AudioRecorder: React.FC<AudioRecorderProps> = ({
  onTranscription,
  onRecordingComplete,
  onError,
  onDeductCredits,
  disabled = false,
  autoTranscribe = true,
  language = 'en',
  variant = 'neutral',
  maxDurationSeconds = MAX_DURATION_SECONDS,
}) => {
  const { user } = useAuth();
  const [state, setState] = useState<RecordingState>({
    isRecording: false,
    isPaused: false,
    isPlaying: false,
    isTranscribing: false,
    duration: 0,
    audioBlob: null,
    audioUrl: null,
  });

  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const audioChunksRef = useRef<Blob[]>([]);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const animationFrameRef = useRef<number | null>(null);
  const audioContextRef = useRef<AudioContext | null>(null);
  const isRecordingRef = useRef<boolean>(false); // Track recording state for animation loop

  // Cleanup on unmount. Must stop the recorder AND its tracks: with
  // ReactFlow's onlyRenderVisibleElements, scrolling a recording Dictation
  // node off-screen unmounts this component — without the explicit stops
  // the mic stays hot (browser indicator on) and the take is lost.
  useEffect(() => {
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
      if (animationFrameRef.current) cancelAnimationFrame(animationFrameRef.current);
      if (state.audioUrl) URL.revokeObjectURL(state.audioUrl);
      if (audioContextRef.current) audioContextRef.current.close();
      const recorder = mediaRecorderRef.current;
      if (recorder) {
        try {
          if (recorder.state !== 'inactive') recorder.stop();
        } catch { /* recorder already stopped */ }
        recorder.stream?.getTracks().forEach((track) => track.stop());
      }
    };
  }, []);

  // Draw waveform visualization
  const drawWaveform = useCallback(() => {
    const canvas = canvasRef.current;
    const analyser = analyserRef.current;
    if (!canvas || !analyser) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const bufferLength = analyser.frequencyBinCount;
    const dataArray = new Uint8Array(bufferLength);
    analyser.getByteTimeDomainData(dataArray);

    ctx.fillStyle = 'rgba(26, 26, 46, 0.8)';
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    ctx.lineWidth = 2;
    ctx.strokeStyle = variant === 'purple' ? '#8b5cf6' : '#10b981';
    ctx.beginPath();

    const sliceWidth = canvas.width / bufferLength;
    let x = 0;

    for (let i = 0; i < bufferLength; i++) {
      const v = dataArray[i] / 128.0;
      const y = (v * canvas.height) / 2;

      if (i === 0) {
        ctx.moveTo(x, y);
      } else {
        ctx.lineTo(x, y);
      }

      x += sliceWidth;
    }

    ctx.lineTo(canvas.width, canvas.height / 2);
    ctx.stroke();

    // Use ref to check recording state (avoids stale closure issue)
    if (isRecordingRef.current) {
      animationFrameRef.current = requestAnimationFrame(drawWaveform);
    }
  }, [variant]);

  // Start recording
  const startRecording = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      
      // Set up audio context for visualization
      audioContextRef.current = new AudioContext();
      const source = audioContextRef.current.createMediaStreamSource(stream);
      analyserRef.current = audioContextRef.current.createAnalyser();
      analyserRef.current.fftSize = 2048;
      source.connect(analyserRef.current);

      // Set up media recorder
      const mediaRecorder = new MediaRecorder(stream, {
        mimeType: 'audio/webm;codecs=opus',
      });
      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };

      mediaRecorder.onstop = () => {
        const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
        const audioUrl = URL.createObjectURL(audioBlob);
        
        setState(prev => ({
          ...prev,
          isRecording: false,
          audioBlob,
          audioUrl,
        }));

        onRecordingComplete?.(audioBlob, audioUrl);

        // Stop all tracks
        stream.getTracks().forEach(track => track.stop());

        // Auto-transcribe if enabled
        if (autoTranscribe) {
          transcribeAudio(audioBlob);
        }
      };

      mediaRecorder.start(100); // Collect data every 100ms

      // Set recording state (both state and ref for animation loop)
      isRecordingRef.current = true;
      setState(prev => ({
        ...prev,
        isRecording: true,
        isPaused: false,
        duration: 0,
        audioBlob: null,
        audioUrl: null,
      }));

      // Start duration timer
      timerRef.current = setInterval(() => {
        setState(prev => ({ ...prev, duration: prev.duration + 1 }));
      }, 1000);

      // Start waveform visualization
      drawWaveform();
    } catch (error) {
      console.error('[AudioRecorder] Failed to start recording:', error);
      onError?.('Failed to access microphone. Please check permissions.');
    }
  }, [autoTranscribe, drawWaveform, onError, onRecordingComplete]);

  // Stop recording
  const stopRecording = useCallback(() => {
    if (mediaRecorderRef.current && state.isRecording) {
      // Stop animation loop first
      isRecordingRef.current = false;
      mediaRecorderRef.current.stop();
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
        animationFrameRef.current = null;
      }
    }
  }, [state.isRecording]);

  // Auto-stop when reaching max duration
  useEffect(() => {
    if (state.isRecording && state.duration >= maxDurationSeconds) {
      stopRecording();
    }
  }, [state.isRecording, state.duration, maxDurationSeconds, stopRecording]);

  // Transcribe audio using Whisper API
  const transcribeAudio = useCallback(async (audioBlob: Blob) => {
    setState(prev => ({ ...prev, isTranscribing: true }));

    // Optimistically deduct credits for transcription (2 credits for whisper-1)
    const TRANSCRIPTION_CREDIT_COST = 2;
    if (onDeductCredits) {
      console.log(`💳 [AudioRecorder] Optimistically deducting ${TRANSCRIPTION_CREDIT_COST} credits for transcription`);
      onDeductCredits(TRANSCRIPTION_CREDIT_COST);
    }

    try {
      const formData = new FormData();
      formData.append('audio', audioBlob, 'recording.webm');
      formData.append('language', language);

      console.log('[AudioRecorder] Sending transcription request...');
      // Build headers - include user ID for server-side credit deduction
      const headers: Record<string, string> = {};
      if (user?.id) {
        headers['x-user-id'] = user.id;
      }
      
      const response = await fetch('/api/openai/transcribe', {
        method: 'POST',
        headers,
        body: formData,
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
        console.error('[AudioRecorder] Transcription failed:', response.status, errorData);
        throw new Error(errorData.error || `Transcription failed with status ${response.status}`);
      }

      const data = await response.json();
      console.log('[AudioRecorder] Transcription successful:', data.text?.substring(0, 100));
      onTranscription?.(data.text);
    } catch (error) {
      console.error('[AudioRecorder] Transcription failed:', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to transcribe audio. Please try again.';
      onError?.(errorMessage);
    } finally {
      setState(prev => ({ ...prev, isTranscribing: false }));
    }
  }, [language, onError, onTranscription, onDeductCredits]);

  // Manual transcribe button
  const handleTranscribe = useCallback(() => {
    if (state.audioBlob) {
      transcribeAudio(state.audioBlob);
    }
  }, [state.audioBlob, transcribeAudio]);

  // Play/pause audio
  const togglePlayback = useCallback(() => {
    if (!audioRef.current || !state.audioUrl) return;

    if (state.isPlaying) {
      audioRef.current.pause();
      setState(prev => ({ ...prev, isPlaying: false }));
    } else {
      audioRef.current.play();
      setState(prev => ({ ...prev, isPlaying: true }));
    }
  }, [state.audioUrl, state.isPlaying]);

  // Clear recording
  const clearRecording = useCallback(() => {
    if (state.audioUrl) {
      URL.revokeObjectURL(state.audioUrl);
    }
    setState({
      isRecording: false,
      isPaused: false,
      isPlaying: false,
      isTranscribing: false,
      duration: 0,
      audioBlob: null,
      audioUrl: null,
    });
  }, [state.audioUrl]);

  // Format duration as MM:SS
  const formatDuration = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  // Handle audio ended
  const handleAudioEnded = useCallback(() => {
    setState(prev => ({ ...prev, isPlaying: false }));
  }, []);

  return (
    <div className={`audio-recorder ${variant}`}>
      {/* Waveform Canvas */}
      <div className="waveform-container">
        <canvas
          ref={canvasRef}
          className="waveform-canvas"
          width={280}
          height={60}
        />
        {!state.isRecording && !state.audioUrl && (
          <div className="waveform-placeholder">
            <MicIcon className="placeholder-icon" />
            <span>Click to record</span>
          </div>
        )}
      </div>

      {/* Duration Display */}
      <div className="duration-display">
        {state.isRecording && (
          <span className="recording-indicator">
            <span className="recording-dot" />
            Recording
          </span>
        )}
        {state.isTranscribing && (
          <span className="transcribing-indicator">
            <span className="transcribing-spinner" />
            Transcribing...
          </span>
        )}
        <span className="duration-time">{formatDuration(state.duration)}</span>
      </div>

      {/* Controls */}
      <div className="recorder-controls">
        {!state.isRecording && !state.audioUrl && (
          <button
            className="control-btn record-btn"
            onClick={startRecording}
            disabled={disabled}
            title="Start recording"
          >
            <MicIcon />
          </button>
        )}

        {state.isRecording && (
          <button
            className="control-btn stop-btn"
            onClick={stopRecording}
            title="Stop recording"
          >
            <StopIcon />
          </button>
        )}

        {state.audioUrl && !state.isRecording && (
          <>
            <button
              className="control-btn play-btn"
              onClick={togglePlayback}
              title={state.isPlaying ? 'Pause' : 'Play'}
            >
              {state.isPlaying ? <PauseIcon /> : <PlayArrowIcon />}
            </button>

            <button
              className="control-btn delete-btn"
              onClick={clearRecording}
              title="Delete recording"
            >
              <DeleteIcon />
            </button>

            {!autoTranscribe && (
              <button
                className="control-btn transcribe-btn"
                onClick={handleTranscribe}
                disabled={state.isTranscribing}
                title="Transcribe audio"
              >
                <SendIcon />
              </button>
            )}
          </>
        )}
      </div>

      {/* Hidden audio element for playback */}
      {state.audioUrl && (
        <audio
          ref={audioRef}
          src={state.audioUrl}
          onEnded={handleAudioEnded}
        />
      )}
    </div>
  );
};

export default AudioRecorder;
